import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Prompt } from "react-router-dom";
import { toaster } from "evergreen-ui";
import update from "immutability-helper";
import deepmerge from "deepmerge";
import apiFetch from "../util/fetch-utils";
import confirm from "../util/components/ConfirmDialog";
import { CURRENT_WORKSPACE_UPDATED_EVENT, WORKSPACE_DELETED_EVENT } from "../../actions/workspaces";
import defaults, { createNestedObject, getOptionalNested } from "../util/defaults";
import { CountryFlag } from "../Workspace/CountryFlag";
import { FormSection } from "./FormSection";
import { GoogleConnections } from "./GoogleConnections";
import { headersMapToRows, rowsToHeadersMap, isIncompleteRow } from "./customHeaders";
import { FREQUENCY_OPTIONS, nearestFrequencyDays } from "./crawlFrequency";
import { validUrl } from "./validateUrl";
import { AddUserModal } from "./AddUserModal";
import { resetStoredHistory, sourceLabel } from "./googleData";
import {
    Shell, Banner, Grid, Field, FieldLabel,
    Input, Select, FieldError, HeaderHint, HeadersPane, HeaderList, HeaderRow,
    HeaderNameInput, HeaderValueInput, FlagRow, UsersRow, UsersList,
    SmallBtnCol, SmallBtn, AddHeaderBtn, EmptyHeaders, EmptyHeadersText,
    ToggleLabel, ToggleTrack, ToggleText, HiddenCheckbox, FooterRow, FooterStatus,
    UnsavedDot, Btn,
} from "./styles";

const UNSAVED_PROMPT = "You have unsaved changes. Leave without saving?";

const wsDefaults = {
    name: "",
    websiteUrl: "",
    users: [],
    countryCode: "",
    crawlerSettings: {
        crawlEnabled: true,
        crawlDelayMillis: 0,
    },
};

const MAX_NAME_LEN = 50;

function WorkspaceAdmin() {
    const dispatch = useDispatch();
    const history = useHistory();
    const currentWorkspace = useSelector(state => state.workspaces.current);
    const login = useSelector(state => state.login.userDetails);

    const [ws, setWs] = useState(() => defaults(currentWorkspace, wsDefaults));
    const [customHeaders, setCustomHeaders] = useState(() =>
        headersMapToRows(getOptionalNested(currentWorkspace, "crawlerSettings.customHeaders", {})));
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [validation, setValidation] = useState({});
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    // Pointing the workspace at a new Google property is three calls: save it, throw the old property's
    // history away, read the new one's back. The block that moved says which source is being read while
    // that runs, then how many rows arrived. Nothing is stored, so a reload clears it.
    const [busy, setBusy] = useState({});
    const [imported, setImported] = useState({});

    // Re-sync the editable copy whenever the stored workspace changes (initial
    // load, and after a successful save re-publishes it to Redux).
    useEffect(() => {
        setWs(defaults(currentWorkspace, wsDefaults));
        setCustomHeaders(headersMapToRows(getOptionalNested(currentWorkspace, "crawlerSettings.customHeaders", {})));
        setIsDirty(false);
    }, [currentWorkspace]);

    // Warn on browser-level navigation (refresh / close / external link).
    // React Router navigation is handled separately by <Prompt>.
    useEffect(() => {
        if (!isDirty) return;
        const handler = event => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    if (!ws) return null;

    const isOwner = ws.ownerName === login.username;
    const crawlEnabled = getOptionalNested(ws, "crawlerSettings.crawlEnabled", false);
    const ignoreRobots = getOptionalNested(ws, "crawlerSettings.filterConfiguration.ignoreRobotsTxt", false);
    const crawlDelayMs = getOptionalNested(ws, "crawlerSettings.crawlDelayMillis", 0);
    const crawlIntervalDays = nearestFrequencyDays(getOptionalNested(ws, "crawlerSettings.crawlIntervalDays", null));
    const headerErrors = validation.customHeaders || [];

    function mergeIntoWorkspace(value) {
        setWs(prev => deepmerge(prev, value));
        setIsDirty(true);
    }

    function handleChange(event, converter) {
        const target = event.target;
        let inputValue = target.checked !== undefined && target.type === "checkbox"
            ? target.checked
            : target.value;
        if (typeof converter === "function") {
            inputValue = converter(inputValue);
        }
        mergeIntoWorkspace(createNestedObject(target.name, inputValue));
    }

    function handleSelectedUserChange({ target }) {
        const selected = [...target.options].filter(o => o.selected).map(o => o.value);
        setSelectedUsers(selected);
    }

    function isValidWorkspace() {
        const validWebsiteUrl = !ws.websiteUrl || ws.websiteUrl.length === 0 || validUrl(ws.websiteUrl);
        const validName = ws.name.length > 0 && ws.name.length <= MAX_NAME_LEN;
        const validCrawlDelay = (!ws.crawlerSettings.crawlDelayMillis || ws.crawlerSettings.crawlDelayMillis >= 0)
            && ws.crawlerSettings.crawlDelayMillis <= 60000;
        const customHeaderErrors = customHeaders.map(isIncompleteRow);
        const validCustomHeaders = !customHeaderErrors.some(Boolean);

        setValidation({
            name: !validName,
            websiteUrl: !validWebsiteUrl,
            crawlDelay: !validCrawlDelay,
            customHeaders: customHeaderErrors,
        });
        return validWebsiteUrl && validName && validCrawlDelay && validCustomHeaders;
    }

    // Takes the workspace to persist, because a connection saved from its dialog has to be written
    // straight away and setWs has not landed yet when it is.
    function save(workspace = ws) {
        if (!isValidWorkspace()) {
            toaster.warning("Save failed", { description: "Please check for invalid fields", duration: 10 });
            return Promise.resolve(false);
        }
        // $set (not merge) so:
        //  - headers removed in the editor actually disappear from the stored map, and
        //  - crawl interval persists the option shown in the select (snapping a legacy
        //    value like 5 to its nearest option, 7), so what's saved matches what's shown.
        const toSave = update(workspace, {
            crawlerSettings: {
                crawlIntervalDays: { $set: crawlIntervalDays },
                customHeaders: { $set: rowsToHeadersMap(customHeaders) },
            },
        });
        setSaving(true);
        return apiFetch("/api/workspaces/" + toSave.id, {
            body: JSON.stringify(toSave),
            method: "PUT",
            headers: { "Content-Type": "application/json" },
        }).then(response => {
            if (response.status !== 200) {
                toaster.danger("Save failed", { description: response.status + " " + response.statusText, duration: 10 });
                return false;
            }
            toaster.success("Saved");
            dispatch({ type: CURRENT_WORKSPACE_UPDATED_EVENT, payload: toSave });
            return true;
        }).finally(() => setSaving(false));
    }

    function deleteWorkspace() {
        apiFetch("/api/workspaces/" + ws.id, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        }).then(response => {
            if (response.status === 200) {
                toaster.success("Workspace deleted");
                history.push("/");
                dispatch({ type: WORKSPACE_DELETED_EVENT, payload: ws });
            } else {
                toaster.danger("Delete failed", { description: response.status + " " + response.statusText, duration: 10 });
            }
        });
    }

    function deleteClicked() {
        confirm({
            title: "Delete Workspace",
            message: (
                <div>
                    <p>Are you sure you want delete this workspace?</p>
                    This will prevent all the users to access <strong>{ws.name}</strong>.
                </div>
            ),
            confirmText: "Ok",
            confirmColor: "primary",
            cancelColor: "link text-danger",
        }).then(confirmed => {
            if (confirmed) deleteWorkspace();
        });
    }

    function removeUsers() {
        confirm({
            title: "Remove workspace users",
            message: (
                <div>
                    <p>Are you sure you want to remove the following users?</p>
                    <ul>
                        {selectedUsers.map((username, i) => <li key={i}>{username}</li>)}
                    </ul>
                    This will prevent the users to access <strong>{ws.name}</strong>.
                </div>
            ),
            confirmText: "Ok",
            confirmColor: "primary",
            cancelColor: "link text-danger",
        }).then(confirmed => {
            if (confirmed) {
                const remainingUsers = ws.users.filter(user => selectedUsers.indexOf(user) < 0);
                setWs(prev => update(prev, { $merge: { users: remainingUsers } }));
                setSelectedUsers([]);
                setIsDirty(true);
            }
        });
    }

    function onAddUserModalExit(value) {
        setAddUserModalOpen(false);
        if (value !== undefined) {
            mergeIntoWorkspace({ users: [value, ...ws.users] });
        }
    }

    // A connection is saved from its own dialog, so it is written on the spot rather than waiting
    // for the page's Save changes.
    function setAnalyticsSettings(settings) {
        saveNow({ analyticsSettings: { $set: settings } }, "analytics", Boolean(settings && settings.propertyId));
    }

    function setSearchConsoleSettings(settings) {
        saveNow({ searchConsoleSettings: { $set: settings } }, "searchConsole", Boolean(settings && settings.siteUrl));
    }

    // A property that moved takes its stored history with it: the old property's rows go, then the new
    // property's twelve months are read back. A property that was removed only wipes - there is nothing
    // left to read from.
    function saveNow(change, source, hasProperty) {
        const workspace = update(ws, change);
        setWs(workspace);
        setImported(current => ({ ...current, [source]: undefined }));
        setBusy(current => ({ ...current, [source]: hasProperty ? "reading" : "removing" }));

        save(workspace)
            .then(saved => saved && resetStoredHistory(source, workspace.seqNumber, hasProperty))
            .then(rows => Number.isInteger(rows) && setImported(current => ({ ...current, [source]: { source, rows } })))
            .catch(error => toaster.danger(`Could not update stored ${sourceLabel(source)} data`, { description: String(error), duration: 10 }))
            .finally(() => setBusy(current => ({ ...current, [source]: undefined })));
    }

    function addCustomHeader() {
        setCustomHeaders([...customHeaders, { name: "", value: "" }]);
        setIsDirty(true);
    }

    function updateCustomHeader(index, field, value) {
        setCustomHeaders(customHeaders.map((header, i) => i === index ? { ...header, [field]: value } : header));
        setIsDirty(true);
    }

    function removeCustomHeader(index) {
        setCustomHeaders(customHeaders.filter((_, i) => i !== index));
        setIsDirty(true);
    }

    return (
        <Shell>
            {!isOwner && login.isLoggedIn === true && (
                <Banner>Only the workspace owner can change these settings.</Banner>
            )}

            <FormSection label="General" description="The website this workspace tracks and how it's identified.">
                <Field>
                    <FieldLabel>Website URL</FieldLabel>
                    <Input
                        type="text"
                        name="websiteUrl"
                        placeholder="https://example.com"
                        value={ws.websiteUrl}
                        onChange={handleChange}
                        disabled={!isOwner}
                        $invalid={validation.websiteUrl}
                    />
                    {validation.websiteUrl && (
                        <FieldError>Please insert a valid URL including protocol. Example: https://example.com</FieldError>
                    )}
                </Field>

                <Grid>
                    <Field>
                        <FieldLabel>Workspace name</FieldLabel>
                        <Input
                            type="text"
                            name="name"
                            value={ws.name}
                            onChange={handleChange}
                            disabled={!isOwner}
                            $invalid={validation.name}
                        />
                        {validation.name && (
                            <FieldError>Name length must be between 1 and {MAX_NAME_LEN} characters</FieldError>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel>Country Code</FieldLabel>
                        <FlagRow>
                            <Input
                                type="text"
                                name="countryCode"
                                value={ws.countryCode}
                                onChange={handleChange}
                                disabled={!isOwner}
                                style={{ flex: 1 }}
                            />
                            <CountryFlag countryCode={ws.countryCode} width={36} />
                        </FlagRow>
                    </Field>
                </Grid>
            </FormSection>

            <FormSection label="Crawler Settings" description="When crawls run, and how the crawler behaves on every request.">
                <Field>
                    <ToggleLabel disabled={!isOwner}>
                        <HiddenCheckbox
                            checked={crawlEnabled}
                            name="crawlerSettings.crawlEnabled"
                            onChange={handleChange}
                            disabled={!isOwner}
                        />
                        <ToggleTrack $on={crawlEnabled} disabled={!isOwner} />
                        <ToggleText>Scheduled crawling enabled</ToggleText>
                    </ToggleLabel>
                </Field>

                {crawlEnabled && (
                    <Field>
                        <FieldLabel>Crawl Frequency</FieldLabel>
                        <Select
                            name="crawlerSettings.crawlIntervalDays"
                            value={crawlIntervalDays}
                            onChange={event => handleChange(event, value => parseInt(value, 10))}
                            disabled={!isOwner}
                        >
                            {FREQUENCY_OPTIONS.map(option => (
                                <option key={option.days} value={option.days}>{option.label}</option>
                            ))}
                        </Select>
                    </Field>
                )}

                <Field $break>
                    <FieldLabel>Crawl Delay (seconds)</FieldLabel>
                    <Input
                        type="number"
                        min="0"
                        max="60"
                        name="crawlerSettings.crawlDelayMillis"
                        value={crawlDelayMs / 1000}
                        onChange={event => handleChange(event, value => parseFloat(value) * 1000)}
                        disabled={!isOwner}
                        $invalid={validation.crawlDelay}
                    />
                    {validation.crawlDelay && (
                        <FieldError>Crawl delay must be between 0 and 60 seconds</FieldError>
                    )}
                </Field>

                <Field>
                    <FieldLabel>Robots.txt</FieldLabel>
                    <ToggleLabel disabled={!isOwner}>
                        <HiddenCheckbox
                            checked={ignoreRobots}
                            name="crawlerSettings.filterConfiguration.ignoreRobotsTxt"
                            onChange={handleChange}
                            disabled={!isOwner}
                        />
                        <ToggleTrack $on={ignoreRobots} disabled={!isOwner} />
                        <ToggleText>Ignore the site's robots.txt rules</ToggleText>
                    </ToggleLabel>
                </Field>

                <Field>
                    <FieldLabel>Custom Request Headers</FieldLabel>
                    <HeaderHint>
                        Sent on every crawl request. Use them to stop the crawler being blocked by a
                        site's security, or to override the headers we send by default.
                    </HeaderHint>
                    <HeadersPane>
                        {customHeaders.length === 0 ? (
                            <EmptyHeaders>
                                <EmptyHeadersText>No custom headers — the crawler sends only its defaults.</EmptyHeadersText>
                                <SmallBtn type="button" disabled={!isOwner} onClick={addCustomHeader}>Add header</SmallBtn>
                            </EmptyHeaders>
                        ) : (
                            <>
                                <HeaderList>
                                    {customHeaders.map((header, i) => (
                                <div key={i}>
                                    <HeaderRow>
                                        <HeaderNameInput
                                            type="text"
                                            placeholder="Header name"
                                            aria-label={"Custom header name " + (i + 1)}
                                            value={header.name}
                                            onChange={e => updateCustomHeader(i, "name", e.target.value)}
                                            disabled={!isOwner}
                                            $invalid={headerErrors[i]}
                                        />
                                        <HeaderValueInput
                                            type="text"
                                            placeholder="Header value"
                                            aria-label={"Custom header value " + (i + 1)}
                                            value={header.value}
                                            onChange={e => updateCustomHeader(i, "value", e.target.value)}
                                            disabled={!isOwner}
                                            $invalid={headerErrors[i]}
                                        />
                                        <SmallBtn
                                            type="button"
                                            disabled={!isOwner}
                                            onClick={() => removeCustomHeader(i)}
                                            aria-label={"Remove custom header " + (i + 1)}
                                        >Remove</SmallBtn>
                                    </HeaderRow>
                                    {headerErrors[i] && <FieldError>Both name and value are required</FieldError>}
                                </div>
                            ))}
                                </HeaderList>
                                <AddHeaderBtn
                                    type="button"
                                    disabled={!isOwner}
                                    onClick={addCustomHeader}
                                >Add header</AddHeaderBtn>
                            </>
                        )}
                    </HeadersPane>
                </Field>
            </FormSection>

            <FormSection label="Access" description="Who owns this workspace and who can access it.">
                <Field>
                    <FieldLabel>Owner</FieldLabel>
                    <Input type="email" name="owner" disabled defaultValue={ws.ownerName} />
                </Field>

                <Field>
                    <FieldLabel>Users</FieldLabel>
                    <UsersRow>
                        <UsersList
                            multiple
                            disabled={!isOwner}
                            value={selectedUsers}
                            onChange={handleSelectedUserChange}
                        >
                            {ws.users.map((user, i) => <option key={i}>{user}</option>)}
                        </UsersList>
                        <SmallBtnCol>
                            <SmallBtn disabled={!isOwner} onClick={() => setAddUserModalOpen(true)}>Add</SmallBtn>
                            <SmallBtn
                                disabled={selectedUsers.length < 1}
                                onClick={removeUsers}
                            >Remove</SmallBtn>
                        </SmallBtnCol>
                    </UsersRow>
                </Field>
            </FormSection>

            <GoogleConnections
                currentWorkspace={ws}
                busy={busy}
                imported={imported}
                setAnalyticsSettings={setAnalyticsSettings}
                setSearchConsoleSettings={setSearchConsoleSettings}
            />

            <FooterRow $dirty={isDirty}>
                <Btn $danger disabled={!isOwner} onClick={deleteClicked}>Delete Workspace</Btn>
                <FooterStatus>
                    {!saving && isDirty && (
                        <>
                            <UnsavedDot aria-hidden />
                            Unsaved changes
                        </>
                    )}
                </FooterStatus>
                <Btn disabled={!isOwner || !isDirty || saving} onClick={() => save()} $emphasised={isDirty}>
                    {saving ? "Saving…" : "Save changes"}
                </Btn>
            </FooterRow>

            <AddUserModal open={addUserModalOpen} onExit={onAddUserModalExit} />
            <Prompt when={isDirty} message={UNSAVED_PROMPT} />
        </Shell>
    );
}

export default WorkspaceAdmin;
